import { api } from '@/lib/api/client';

/**
 * Los HECHOS de un evento tal cual los cuenta el servidor, sin porcentajes.
 * Refleja `EventFacts` de `ox-platform` (event-facts.service.ts). Todo lo que
 * sea tasa, lectura o «pendiente» lo deriva `engine.ts` sobre esto.
 */
export interface EventFactsLedgerRow {
  stockId: number;
  name: string;
  image: string | null;
  unitCost: number | null;
  restockThreshold: number | null;
  /** Σ quantity: lo repartido a las estaciones del evento. */
  allocated: number;
  /** Σ available: lo que aún puede ganarse. */
  inPlay: number;
  /** Con registro de canje (pendiente + entregado + caducado). */
  won: number;
  delivered: number;
  pending: number;
  expired: number;
  voided: number;
  /** delivered × unitCost; null si el artículo no tiene coste. */
  distributedValue: number | null;
  /** Devueltas al almacén al cerrar el evento. */
  returned: number;
  /** Σ expectedQuantity (calculadora, CH-09), o null si nadie lo fijó. */
  expected?: number | null;
  /** Unidades sin destino que ya tienen una disposición: se restan de «sin destino». */
  dispositioned?: number;
}

export interface EventFactsFunnel {
  interactions: number;
  responses: number;
  contacts: number;
  leads: number;
  marketingOptIns: number;
  prizesWon: number;
  prizesDelivered: number;
}

export interface EventFactsWeatherDay {
  day: string;
  tempMinC: number | null;
  tempMaxC: number | null;
  weatherCode: number | null;
  precipitationMm: number | null;
}

export interface EventFacts {
  event: {
    id: number;
    name: string;
    address: string | null;
    startDate: string | null;
    endDate: string | null;
    eventStatus: string | null;
    eventGroupId: number | null;
    companyId: number | null;
  };
  funnel: EventFactsFunnel;
  ledger: EventFactsLedgerRow[];
  /** Capturas por bloque de 15 minutos (primer toque de cada sesión), UTC. */
  throughput: Array<{ at: string; count: number }>;
  weather: EventFactsWeatherDay[];
  /** Metas que rigen: las del evento y, si no fijó una, la del proyecto entera. */
  goals: Array<{ metric: string; target: number; source?: 'event' | 'project' }>;
  stations: { total: number; withSurvey: number };
  /** El acta de cierre, si el evento se cerró. */
  closing: {
    closedAt: string;
    closedBy: string | null;
    note: string | null;
    returned: number;
    restocked: number;
    unaccounted: number;
    counted: number;
  } | null;
}

export interface EventFactsResponse {
  events: EventFacts[];
  uniqueContacts: number;
  repeatContacts: number;
}

export const factsApi = {
  /** Hechos de varios eventos de una vez; lo que no es de la empresa no vuelve. */
  forEvents: (eventIds: readonly number[]) =>
    api.get<EventFactsResponse>('/xk-event-insights/facts', {
      params: { ids: [...eventIds].sort((a, b) => a - b).join(',') },
    }),
};
